import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import {
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth';
import {
  Timestamp,
  addDoc,
  collection,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  type DocumentData,
  type Firestore,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';

let appPromise: Promise<FirebaseApp> | null = null;
const getApp = () => {
  if (!appPromise) {
    appPromise = fetch('/__/firebase/init.json').then(async (res) => {
      if (!res.ok) throw new Error(`Failed to load Firebase config: HTTP ${res.status}`);
      const config = (await res.json()) as FirebaseOptions;
      return initializeApp(config);
    });
  }
  return appPromise;
};

let dbPromise: Promise<Firestore> | null = null;
const getDb = () => {
  if (!dbPromise) dbPromise = getApp().then((app) => getFirestore(app));
  return dbPromise;
};

let authPromise: Promise<Auth> | null = null;
const getAuthInstance = () => {
  if (!authPromise) authPromise = getApp().then((app) => getAuth(app));
  return authPromise;
};

export type QuestionnaireResponse = {
  rating: number;
  comment: string;
};

export const saveResponse = async (response: QuestionnaireResponse): Promise<void> => {
  const db = await getDb();
  await addDoc(collection(db, 'responses'), {
    rating: response.rating,
    comment: response.comment,
    createdAt: serverTimestamp(),
  });
};

export type StoredResponse = {
  id: string;
  rating: number;
  comment: string;
  createdAt: Date | null;
};

const toStoredResponse = (d: QueryDocumentSnapshot<DocumentData>): StoredResponse => {
  const data = d.data();
  const ts = data.createdAt;
  return {
    id: d.id,
    rating: typeof data.rating === 'number' ? data.rating : 0,
    comment: typeof data.comment === 'string' ? data.comment : '',
    createdAt: ts instanceof Timestamp ? ts.toDate() : null,
  };
};

export const subscribeResponses = (
  onUpdate: (responses: StoredResponse[]) => void,
  onError: (err: Error) => void,
  max = 500,
): (() => void) => {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;

  getDb().then((db) => {
    if (cancelled) return;
    const q = query(collection(db, 'responses'), orderBy('createdAt', 'desc'), limit(max));
    unsubscribe = onSnapshot(
      q,
      (snap) => onUpdate(snap.docs.map(toStoredResponse)),
      (err) => onError(err),
    );
  }).catch((err: unknown) => {
    if (!cancelled) onError(err instanceof Error ? err : new Error(String(err)));
  });

  return () => {
    cancelled = true;
    if (unsubscribe) unsubscribe();
  };
};

export const signInAdmin = async (): Promise<void> => {
  const auth = await getAuthInstance();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ hd: 'esprot.co.jp' });
  await signInWithPopup(auth, provider);
};

export const signOutAdmin = async (): Promise<void> => {
  const auth = await getAuthInstance();
  await signOut(auth);
};

export const onAdminAuthStateChanged = (callback: (user: User | null) => void): (() => void) => {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;
  getAuthInstance().then((auth) => {
    if (cancelled) return;
    unsubscribe = onAuthStateChanged(auth, callback);
  });
  return () => {
    cancelled = true;
    if (unsubscribe) unsubscribe();
  };
};
